import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage, useFormikContext, FormikHelpers } from "formik";
import * as Yup from "yup";
import Modal from "../../../components/Modal";
import { Dropdown } from "../../../components/Dropdown";
import Switch from "../../../components/Switch";
import { Button } from "../../../components/Button";

// ✅ Import JSON data
import indiaData from "../../../data/stateData.json";
import { apiPost, apiPut } from "../../../services/apiServices";
import { endPoints } from "../../../configs/apiUrls";
import { toast } from "react-toastify";

// Define the unified type locally or import it
export interface UnifiedLocationType {
  _id?: string;
  state: string;
  city: string;
  area: string;
  isActive: boolean;
}

interface AddLocationProps {
  mode: "add" | "edit";
  initialData?: UnifiedLocationType;
  onSubmit: (data: any) => void;
  onCancel?: () => void;
  isOpen: boolean;
  setShowAddModal: (val: boolean) => void;
}

const LocationForm: React.FC<{
  mode: "add" | "edit";
  stateOptions: { label: string; value: string }[];
  cityOptions: { label: string; value: string; state: string }[];
  setShowAddModal: any;
  showLoader: boolean;
  handleCancel?: () => void;
}> = ({ mode, stateOptions, cityOptions, setShowAddModal, handleCancel, showLoader }) => {
  const { values, setFieldValue } = useFormikContext<UnifiedLocationType>();
  const [filteredCityOptions, setFilteredCityOptions] = useState<{ label: string; value: string }[]>([]);

  // ✅ Filter cities dynamically based on state selection
  useEffect(() => {
    if (values.state) {
      const filtered = cityOptions
        .filter((c) => c.state === values.state)
        .map(({ label, value }) => ({ label, value }));
      setFilteredCityOptions(filtered);

      if (!filtered.find((c) => c.value === values.city)) {
        setFieldValue("city", "");
      }
    } else {
      setFilteredCityOptions([]);
      setFieldValue("city", "");
    }
  }, [values.state, cityOptions, setFieldValue]);

  return (
    <Form className="w-full max-w-md mx-auto bg-white rounded-lg">

      {/* Header */}
      <div className="border-b border-gray-200 pb-4 mb-5">
        <h2 className="text-xl font-bold text-gray-800">
          {mode === "add" ? "Add New Area" : "Edit Area"}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {mode === "add"
            ? "Select a state and city, then enter the area details."
            : "Update the details for this area."}
        </p>
      </div>

      {/* Form Fields (Stacked vertically for perfect alignment) */}
      <div className="space-y-5">

        {/* State */}
        <div className="flex flex-col">
          <label className="text-sm font-semibold text-gray-700 mb-1.5">
            State <span className="text-red-500">*</span>
          </label>
          <div className="w-full">
            <Dropdown
              options={stateOptions}
              value={values.state}
              onChange={(val) => setFieldValue("state", val)}
              placeholder="Select state"
            />
          </div>
          <ErrorMessage name="state" component="div" className="text-red-500 text-xs mt-1 font-medium" />
        </div>

        {/* City */}
        <div className="flex flex-col">
          <label className="text-sm font-semibold text-gray-700 mb-1.5">
            City <span className="text-red-500">*</span>
          </label>
          <div className="w-full">
            <Dropdown
              options={filteredCityOptions}
              value={values.city}
              onChange={(val) => setFieldValue("city", val)}
              placeholder="Select city"
              disabled={!values.state}
            />
          </div>
          <ErrorMessage name="city" component="div" className="text-red-500 text-xs mt-1 font-medium" />
        </div>

        {/* Area Name */}
        <div className="flex flex-col">
          <label className="text-sm font-semibold text-gray-700 mb-1.5">
            Area Name <span className="text-red-500">*</span>
          </label>
          <Field
            name="area"
            placeholder="e.g. Koramangala, Andheri West"
            className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm"
          />
          <ErrorMessage name="area" component="div" className="text-red-500 text-xs mt-1 font-medium" />
        </div>

        {/* Active Toggle (Edit Mode Only) */}
        {mode === "edit" && (
          <div className="bg-gray-50 p-3 rounded-md border border-gray-200 mt-2">
            <Switch
              checked={values.isActive ?? true}
              onCheckedChange={(val) => setFieldValue("isActive", val)}
              label="Is Area Active?"
            />
          </div>
        )}
      </div>

      {/* Footer / Buttons */}
      <div className="flex justify-end gap-3 pt-5 mt-6 border-t border-gray-200">
        <Button
          onClick={() => {
            setShowAddModal(false);
            if (handleCancel) handleCancel();
          }}
          variant="error"
          type="button"
          className="px-5 py-2 text-sm"
        >
          Cancel
        </Button>
        <Button disabled={showLoader} type="submit" className="px-6 py-2 text-sm shadow-sm">
          {showLoader ? "Saving..." : mode === "add" ? "Create Area" : "Save Changes"}
        </Button>
      </div>
    </Form>
  );
};

const AddLocationModal: React.FC<AddLocationProps> = ({
  mode = "add",
  initialData,
  onSubmit,
  onCancel,
  isOpen,
  setShowAddModal,
}) => {
  const [showLoader, setShowLoader] = useState(false);

  const stateOptions = indiaData.map((s) => ({ label: s.state, value: s.state }));
  const cityOptions = indiaData.flatMap((s) =>
    s.districts.map((d) => ({ label: d, value: d, state: s.state }))
  );

  const initialValues: UnifiedLocationType = {
    _id: initialData?._id ?? "",
    state: initialData?.state ?? "",
    city: initialData?.city ?? "",
    area: initialData?.area ?? "",
    isActive: initialData?.isActive ?? true,
  };

  const validationSchema = Yup.object({
    state: Yup.string().required("State is required"),
    city: Yup.string().required("City is required"),
    area: Yup.string().required("Area name is required"),
  });

  // Include FormikHelpers to manipulate form state dynamically
  const handleSaveLocation = async (
    values: UnifiedLocationType,
    { setFieldValue }: FormikHelpers<UnifiedLocationType>
  ) => {
    setShowLoader(true);
    try {
      const res = mode === "edit"
        ? await apiPut(`${endPoints.CREATE_LOCATION}/${initialData?._id}`, values)
        : await apiPost(endPoints.CREATE_LOCATION, values);

      if (res?.success) {
        toast.success(res.message || "Location saved successfully!");
        // Notify parent component to refresh table data
        onSubmit(values);
        if (mode === "edit") {
          // Close modal entirely on edit
          setShowAddModal(false);
        } else {
          // Add mode: Clear only the area name so they can rapidly add another area
          setFieldValue("area", "");
        }
      } else {
        toast.error(res.data?.message || "Failed to save location");
      }
    } catch (error: any) {
      toast.error(error.data?.message || "Something went wrong");
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <Modal onBackdropClick = {() =>{
      
    }} isOpen={isOpen} onClose={() => setShowAddModal(false)}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={handleSaveLocation}
      >
        <LocationForm
          mode={mode}
          stateOptions={stateOptions}
          cityOptions={cityOptions}
          setShowAddModal={setShowAddModal}
          handleCancel={onCancel}
          showLoader={showLoader}
        />
      </Formik>
    </Modal>
  );
};

export default AddLocationModal;